import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/format';
import { PathIcon, P } from './Icon';

/**
 * Bottom sheet on mobile, centered modal (or right panel with `side`) on desktop.
 * The designs use a 14px top radius on mobile sheets.
 */
export function Sheet({ open, onClose, children, title, side, className, maxWidth = 560 }: { open: boolean; onClose: () => void; children: ReactNode; title?: ReactNode; side?: boolean; className?: string; maxWidth?: number }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center lg:items-center" role="dialog" aria-modal>
      <div className="absolute inset-0 animate-fade bg-bg/75" onClick={onClose} />
      <div
        className={cx(
          'relative flex max-h-[90dvh] w-full animate-sheet flex-col overflow-hidden rounded-t-[14px] border border-border bg-surface lg:animate-rise lg:rounded-[10px]',
          side && 'lg:absolute lg:inset-y-0 lg:right-0 lg:max-h-none lg:rounded-none lg:border-y-0 lg:border-r-0',
          className,
        )}
        style={{ maxWidth: side ? 440 : maxWidth }}
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border lg:hidden" />
        {title != null && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="display min-w-0 text-[18px] font-semibold">{title}</div>
            <button onClick={onClose} className="flex size-10 items-center justify-center rounded-[6px] text-muted hover:bg-raised hover:text-text" aria-label="Close" data-guide="sheet-close">
              <PathIcon d={P.x} />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden safe-bottom">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
